import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { Edit2 } from 'lucide-react';
import * as payrollApi from '../../../../services/api/payrollApi';
import Callout from '../../Common/Callout';
import UpdateVacationBalanceModal from './UpdateVacationBalanceModal';
import { Section, Facts, Fact, SectionEmpty } from './EmployeeDetailParts';

/**
 * VacationBalanceCard — per-employee semester saldo on the employee profile.
 *
 * Read-only view with a "Redigera" button that opens UpdateVacationBalanceModal
 * for admin-set field edits (earned / carried / saved). Matches the pattern
 * used by CompensationCard + TaxProfileCard on the same page.
 *
 * Backend: GET /payroll/vacation-balance?employeeId=... (load)
 *          PUT /payroll/vacation-balance (save, from the modal)
 */
const VacationBalanceCard = ({ employeeId, companyId }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await payrollApi.getVacationBalance(companyId, { employeeId });
      setSnapshot(res.data?.snapshot || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda semestersaldo.');
    } finally {
      setLoading(false);
    }
  }, [companyId, employeeId]);

  useEffect(() => { load(); }, [load]);

  const hasAdvance = (snapshot?.advanceDaysTaken || 0) > 0;

  return (
    <>
      <Section
        id="vacation-balance-heading"
        title="Semestersaldo"
        meta={loading
          ? 'Hämtar…'
          : snapshot
            ? `Semesterår ${snapshot.year}/${snapshot.year + 1} (${snapshot.dateRange.from} – ${snapshot.dateRange.to})`
            : 'Ingen data för nuvarande semesterår'}
        actions={
          <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" onClick={() => setShowModal(true)} disabled={!snapshot} aria-label="Redigera semestersaldo">
            <Edit2 />
            Ändra
          </button>
        }
      >
        {loading && (
          <div className="text-muted" style={{ fontSize: '0.875rem' }}>
            <Spinner animation="border" size="sm" className="me-2" />
            Laddar semestersaldo…
          </div>
        )}

        {error && <Callout tone="danger" role="alert">{error}</Callout>}

        {snapshot && (
          <>
            <Facts tight>
              <Fact label="Tjänat" hint="Intjänade dagar under föregående intjänandeår (semesterlagen 4 och 7 §§).">{Number(snapshot.earnedDays || 0)}</Fact>
              <Fact label="Överförda" hint="Överförda från tidigare år.">{Number(snapshot.carriedOverDays || 0)}</Fact>
              <Fact label="Sparat" hint="Sparade dagar (semesterlagen 18 §, högst fem per år).">{Number(snapshot.savedDays || 0)}</Fact>
              <Fact label="Använd" hint="Beräknas från godkända semesterposter på lönespecifikationer.">{Number(snapshot.usedDays || 0)}</Fact>
              <Fact label="Kvar" tone={snapshot.remainingPaidDays > 0 ? 'strong' : undefined} hint="Tjänat + överförda − använd, lägst noll.">{Number(snapshot.remainingPaidDays || 0)}</Fact>
              <Fact label="Förskott" tone={hasAdvance ? 'warning' : undefined} hint="Dagar uttagna över det betalda taket. Tillåts, men bokförs som förskott.">{Number(snapshot.advanceDaysTaken || 0)}</Fact>
            </Facts>

            {hasAdvance && (
              <Callout tone="warning" className="mt-3 mb-0">
                Den anställda har tagit ut {snapshot.advanceDaysTaken} dagar mer än det betalda taket.
                Hanteras som förskott och behöver regleras vid slutlön.
              </Callout>
            )}
          </>
        )}

        {!loading && !snapshot && !error && (
          <SectionEmpty>
            Kör ”Intjäna 7 § (alla)” under Lön → Semester för att initiera saldot, eller ange värden via Ändra.
          </SectionEmpty>
        )}
      </Section>

      <UpdateVacationBalanceModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        employeeId={employeeId}
        companyId={companyId}
        snapshot={snapshot}
        onSuccess={load}
      />
    </>
  );
};

export default VacationBalanceCard;
